<template>
  <div class="instagram-analytics-public-filter">
    <el-row class="mb-x2" :gutter="20">
      <el-col :span="8">
        <el-select class="prize-select mr-x2" v-model="prizeId">
          <el-option v-for="(prize, index) in prizes" :value="prize.id" :label="prize.name" :key="index"></el-option>
        </el-select>
      </el-col>
      <el-col class="date-time-picker" :span="8">
        <AnalyticsPeriodPicker
          v-model="dateTimeRange"
          :campaign-start-datetime="campaignStartDatetime"
          :campaign-end-datetime="campaignEndDatetime"
        ></AnalyticsPeriodPicker>
      </el-col>
    </el-row>
  </div>
</template>

<script lang="ts">
import moment from 'moment-timezone'
import { storeToRefs } from 'pinia'
import { computed, defineComponent, watch } from 'vue'

import AnalyticsPeriodPicker from '@/client/components/_atoms/analytics_period_picker'
import { useInstagramInstantwinAnalyticsPublicStore } from '@/client/features/instagram_instantwin_analytics_public/hooks/instagramInstantwinAnalyticsPublicStore'
import { getAnalyticsDefaultPeriod } from '@/common/utils/analytics_period'

export default defineComponent({
  components: {
    AnalyticsPeriodPicker
  },
  setup() {
    const store = useInstagramInstantwinAnalyticsPublicStore()
    const { campaign, params, prizes: storePrizes } = storeToRefs(store)

    const campaignStartDatetime = computed(() => campaign.value?.startDatetime ?? null)
    const campaignEndDatetime = computed(() => campaign.value?.endDatetime ?? null)

    const prizes = computed(() => {
      if (storePrizes.value.length === 1) {
        return storePrizes.value
      }

      return [{ id: '', name: '全ての応募グループ' }, ...storePrizes.value]
    })

    const prizeId = computed({
      get: () => params.value.prizeId,
      set: (nextPrizeId: number | '') => {
        if (nextPrizeId === params.value.prizeId) {
          return
        }

        store.setParams({ prizeId: nextPrizeId })
        void store.fetchCampaignAnalytics()
      }
    })

    const dateTimeRange = computed({
      get: () => [params.value.startDatetime, params.value.endDatetime],
      set: (datetime: any) => {
        if (!datetime) {
          if (!campaign.value) {
            return
          }

          datetime = getAnalyticsDefaultPeriod(campaign.value.startDatetime, campaign.value.endDatetime)
        }

        const startDatetime = moment(datetime[0])
        const endDatetime = moment(datetime[1])

        const paramStartDatetime = moment(params.value.startDatetime)
        const paramEndDatetime = moment(params.value.endDatetime)

        store.setParams({
          startDatetime: startDatetime.format('YYYY-MM-DD'),
          endDatetime: endDatetime.format('YYYY-MM-DD')
        })

        if (paramStartDatetime.isSame(startDatetime) && paramEndDatetime.isSame(endDatetime)) {
          return
        }

        void store.fetchCampaignAnalytics()
      }
    })

    watch(
      storePrizes,
      nextPrizes => {
        if (nextPrizes.length !== 1) {
          return
        }

        const onlyPrizeId = nextPrizes[0]?.id
        if (!onlyPrizeId || params.value.prizeId === onlyPrizeId) {
          return
        }

        store.setParams({ prizeId: onlyPrizeId })

        if (params.value.startDatetime && params.value.endDatetime) {
          void store.fetchCampaignAnalytics()
        }
      },
      { immediate: true }
    )

    return {
      campaignStartDatetime,
      campaignEndDatetime,
      prizes,
      prizeId,
      dateTimeRange
    }
  }
})
</script>

<style lang="scss" scoped>
.instagram-analytics-public-filter {
  :deep(.prize-select),
  :deep(.prize-select .el-input) {
    width: 100%;
  }

  :deep(.date-time-picker) {
    .el-range-editor {
      width: 100% !important;
    }

    float: right;
  }
}
</style>
